import { useState } from 'react'
import { Alert, Button, Group, Loader, Modal, Radio, ScrollArea, Stack, Text, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconUsers } from '@tabler/icons-react'
import { RestrictedState } from '../access/RestrictedState'
import { useHandleSignal, useSignalAssignees } from '../../hooks/useSignals'
import type { AdminGate } from '../../utils/adminGate'
import {
  ASSIGNEE_NOT_NOTIFIED, HANDLING_REFUSED_FALLBACK, assigneeView, handlingErrorView, handlingToast, noDirectoryText,
  takeOverConfirmText, takeOverLabel,
} from './signalHandlingFormat'
import { signalTitle } from './signalFormat'
import type { Signal } from '../../types/signals'

/**
 * `ObservatorySignalHandlingController.AssigneeWindowDays` — said before the list has loaded; once it has, the
 * served `windowDays` is what the text uses.
 */
const ASSIGNEE_WINDOW_DAYS = 90

/** More rows than this and the list gets a name filter. */
const FILTER_FROM = 8

type AssignableSignal = Pick<Signal, 'id' | 'ruleId' | 'ruleTitle' | 'subjectLabel' | 'handling'>

/**
 * Hand a signal to somebody else (D6b) — chosen from the people the AUDIT TRAIL knows, because there is no directory
 * to search (no Graph consent). The server lists everyone who performed an Observatory write in the last 90 days,
 * the caller excluded, and an assignment sends that list's user id: a typed name is never an identity, and the name
 * stamped on the row is the one the assignee presented.
 *
 * Four states, each its own: loading, failed (a 403 is the restricted state; the rest by status), empty ("nobody
 * Observatory can name") and ready. It says the assignee is NOT notified. An error keeps the dialog open with the
 * choice intact; a HandlingChanged refusal offers "Assign it anyway" with the fresh revision, after a confirmation.
 */
export function AssignSignalModal({ signal, gate, opened, onClose }: {
  signal: AssignableSignal | null
  gate: AdminGate
  opened: boolean
  onClose: () => void
}) {
  return (
    <Modal opened={opened && signal !== null} onClose={onClose} title="Hand this signal to someone" size="lg">
      {/* Mounted only while open, so the choice and any error start fresh each time. */}
      {opened && signal && <AssignSignalModalBody signal={signal} gate={gate} onDone={onClose} />}
    </Modal>
  )
}

/**
 * The dialog's contents. Exported for the render tests: Mantine's Modal portals its children, and a portal renders
 * nothing under react-dom/server.
 */
export function AssignSignalModalBody({ signal, gate, onDone }: {
  signal: AssignableSignal
  gate: AdminGate
  onDone: () => void
}) {
  const assignees = useSignalAssignees(gate)
  const act = useHandleSignal()
  const [selected, setSelected] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [confirming, setConfirming] = useState(false)

  const view = assigneeView(assignees)
  const windowDays = assignees.data?.windowDays ?? ASSIGNEE_WINDOW_DAYS
  const error = act.isError ? handlingErrorView(act.error) : null
  const revision = signal.handling?.revision ?? null

  const send = (expectedRevision: number) => {
    if (!selected) return
    setConfirming(false)
    act.mutate(
      { verb: 'assign', id: signal.id, expectedRevision, assigneeUserId: selected },
      {
        onSuccess: (result) => {
          const { title, message, color } = handlingToast('assign', result)
          notifications.show({ title, message, color })
          onDone()
        },
      },
    )
  }

  const header = (
    <Stack gap={4}>
      <Text size="sm" fw={500}>{signalTitle(signal)}</Text>
      <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>{signal.subjectLabel}</Text>
      <Text size="sm">{noDirectoryText(windowDays)}</Text>
      <Text size="sm" fw={500} data-assign-not-notified="">{ASSIGNEE_NOT_NOTIFIED}</Text>
    </Stack>
  )

  if (gate.state !== 'admin') {
    return (
      <Stack gap="md" data-assign-state="not-offered">
        {header}
        <Text size="sm" c="dimmed">
          {gate.state === 'denied' ? (gate.reason.trim() || HANDLING_REFUSED_FALLBACK) : 'Not offered: this session has not been told it may handle signals.'}
        </Text>
      </Stack>
    )
  }

  const rows = view.kind === 'ready'
    ? view.rows.filter((r) => !filter.trim() || r.name.toLowerCase().includes(filter.trim().toLowerCase()))
    : []
  const chosen = view.kind === 'ready' ? view.rows.find((r) => r.userId === selected) ?? null : null

  return (
    <Stack gap="md" data-assign-state={view.kind}>
      {header}

      {view.kind === 'loading' && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">Reading who Observatory can name…</Text>
        </Group>
      )}

      {view.kind === 'restricted' && <RestrictedState surface="The list of people a signal can be handed to" compact />}

      {view.kind === 'error' && (
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title={view.title}>
          <Text size="sm">{view.message}</Text>
        </Alert>
      )}

      {view.kind === 'empty' && (
        <Group gap={8} wrap="nowrap" align="flex-start">
          <IconUsers size={18} stroke={1.5} style={{ flexShrink: 0, color: 'var(--color-text-disabled)' }} />
          <Stack gap={2}>
            <Text size="sm" fw={500}>{view.title}</Text>
            <Text size="sm" c="dimmed">{view.message}</Text>
          </Stack>
        </Group>
      )}

      {view.kind === 'ready' && (
        <Stack gap="xs">
          {view.rows.length > FILTER_FROM && (
            <TextInput
              placeholder="Filter by name"
              aria-label="Filter by name"
              value={filter}
              onChange={(event) => setFilter(event.currentTarget.value)}
            />
          )}
          <ScrollArea.Autosize mah={320}>
            <Radio.Group value={selected} onChange={setSelected} name="assignee" aria-label="Assignee">
              <Stack gap={8}>
                {rows.map((row) => (
                  <Radio key={row.userId} value={row.userId} label={row.name} description={row.lastActed} data-assignee="" />
                ))}
                {rows.length === 0 && <Text size="sm" c="dimmed">Nobody in the list matches “{filter.trim()}”.</Text>}
              </Stack>
            </Radio.Group>
          </ScrollArea.Autosize>
          {view.caption && <Text size="xs" c="dimmed">{view.caption}</Text>}
        </Stack>
      )}

      {error && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light" title={error.title} data-handling-error={error.kind}>
          <Stack gap={6}>
            <Text size="sm">{error.message}</Text>
            {error.kind === 'changed' && error.freshRevision != null && selected && (
              confirming ? (
                <Stack gap={6}>
                  <Text size="sm">{takeOverConfirmText('assign', error.current)}</Text>
                  <Group gap="xs">
                    <Button size="xs" color="red" onClick={() => send(error.freshRevision!)}>{takeOverLabel('assign')}</Button>
                    <Button size="xs" variant="subtle" onClick={() => setConfirming(false)}>Keep theirs</Button>
                  </Group>
                </Stack>
              ) : (
                <Group gap="xs">
                  <Button size="xs" variant="light" onClick={() => setConfirming(true)}>{takeOverLabel('assign')}…</Button>
                </Group>
              )
            )}
          </Stack>
        </Alert>
      )}

      <Group justify="flex-end">
        <Button variant="subtle" onClick={onDone}>Cancel</Button>
        <Button
          disabled={view.kind !== 'ready' || !chosen || revision == null || act.isPending}
          loading={act.isPending}
          onClick={() => revision != null && send(revision)}
        >
          {chosen ? `Assign to ${chosen.name}` : 'Assign'}
        </Button>
      </Group>
    </Stack>
  )
}
